import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import {
    Loader2,
    ShieldAlert,
    Package,
    Tag,
    MapPin,
    Calendar,
    MessageSquare,
    Send,
    GraduationCap,
    UserCheck,
    CheckCircle,
    Lock,
    Circle,
    CircleDot,
    CircleCheck,
} from 'lucide-react';
import toast from 'react-hot-toast';
import axiosInstance from '../../utils/axiosConfig';
import Modal from '../common/Modal';

const API = '/api/calidad/sura-reporta';

// Estados del seguimiento, en los tonos discretos de Tickets.
export const ESTADOS_REPORTE = {
    pendiente: { label: 'Pendiente', icon: Circle, color: '#64748b', bg: '#f1f5f9' },
    en_revision: { label: 'En revisión', icon: CircleDot, color: '#0062A5', bg: '#eff6ff' },
    cerrado: { label: 'Cerrado', icon: CircleCheck, color: '#047857', bg: '#ecfdf5' },
};

export const BadgeEstado = ({ estado, label }) => {
    const e = ESTADOS_REPORTE[estado];
    if (!e) return null;
    const Icon = e.icon;
    return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-medium" style={{ color: e.color, background: e.bg }}>
            <Icon size={12} />
            {label || e.label}
        </span>
    );
};

const fechaHora = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return isNaN(d) ? iso : d.toLocaleString('es-VE', { day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
};
const etiqueta = (v) => (v == null ? '' : typeof v === 'string' ? v : (v.label || v.nombre || v.id || ''));
const urlFoto = (f) => {
    const ruta = typeof f === 'string' ? f : (f?.url || f?.ruta || '');
    if (!ruta) return null;
    return ruta.startsWith('/api') || ruta.startsWith('http') ? ruta : `/api${ruta.startsWith('/') ? '' : '/'}${ruta}`;
};

const Dato = ({ icon: Icon, children }) => (
    <p className="flex items-start gap-2 text-sm text-gray-700">
        <Icon size={15} className="text-gray-400 mt-0.5 shrink-0" />
        <span>{children}</span>
    </p>
);

// Que se reporto, en palabras: la linea del producto y cada tipo de desviacion
// con lo que significa (o lo que escribio la persona, si es "Otro").
export const QueSeReporto = ({ linea, desviaciones, className = 'text-xs' }) => (
    <div className={`space-y-1 ${className}`}>
        {linea && (
            <p><span className="text-gray-400">Línea:</span> <span className="font-medium text-gray-800">{linea}</span></p>
        )}
        {desviaciones?.length > 0 && (
            <div>
                <span className="text-gray-400">{desviaciones.length > 1 ? 'Tipos de desviación:' : 'Tipo de desviación:'}</span>
                <ul className="mt-0.5 space-y-0.5">
                    {desviaciones.map(x => (
                        <li key={x.id}>
                            <span className="font-medium text-gray-800">{x.label}</span>
                            {x.descripcion && <span className="text-gray-500"> — {x.descripcion}</span>}
                        </li>
                    ))}
                </ul>
            </div>
        )}
    </div>
);

const DetalleReporte = ({ diagnosticoId, onClose, onCambio }) => {
    const [d, setD] = useState(null);
    const [cargando, setCargando] = useState(true);
    const [mensaje, setMensaje] = useState('');
    const [enviando, setEnviando] = useState(false);
    const [accion, setAccion] = useState(null); // 'cerrar'
    const [cerrando, setCerrando] = useState(false);
    const [nota, setNota] = useState('');
    const [visor, setVisor] = useState(null);
    const finRef = useRef(null);
    // El padre puede pasar onClose como funcion nueva en cada render: con la ref, el
    // detalle no se recarga (ni borra lo que se esta escribiendo) cuando la lista refresca.
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    const cargar = useCallback(async () => {
        try {
            const res = await axiosInstance.get(`${API}/diagnosticos/${diagnosticoId}`);
            setD(res.data);
        } catch (error) {
            toast.error(error.response?.data?.error || 'No se pudo cargar el reporte');
            onCloseRef.current();
        } finally {
            setCargando(false);
        }
    }, [diagnosticoId]);

    useEffect(() => {
        setCargando(true);
        setD(null);
        setCerrando(false);
        setNota('');
        setMensaje('');
        cargar();
    }, [cargar]);

    useEffect(() => {
        if (finRef.current) finRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, [d?.comentarios?.length]);

    const tras = async () => {
        await cargar();
        onCambio?.();
    };

    const cerrar = async () => {
        if (!nota.trim()) {
            toast.error('Escribe la nota de cierre.');
            return;
        }
        setAccion('cerrar');
        try {
            await axiosInstance.post(`${API}/diagnosticos/${diagnosticoId}/cerrar`, { nota: nota.trim() });
            toast.success('Reporte cerrado');
            setCerrando(false);
            setNota('');
            await tras();
        } catch (error) {
            toast.error(error.response?.data?.error || 'No se pudo cerrar el reporte');
        } finally {
            setAccion(null);
        }
    };

    const enviar = async () => {
        if (!mensaje.trim() || enviando) return;
        setEnviando(true);
        try {
            await axiosInstance.post(`${API}/diagnosticos/${diagnosticoId}/comentarios`, { mensaje: mensaje.trim() });
            setMensaje('');
            await tras();
        } catch (error) {
            toast.error(error.response?.data?.error || 'No se pudo enviar el mensaje');
        } finally {
            setEnviando(false);
        }
    };

    const cerrado = d?.estado === 'cerrado';
    const conSeguimiento = Boolean(d?.hay_falla);
    const persona = d?.persona || {};
    const fotos = (d?.fotos || []).map(urlFoto).filter(Boolean);
    const comentarios = d?.comentarios || [];

    return (
        <>
            <Modal isOpen onClose={onClose} size="xl" closeOnEscape={!visor}>
                <Modal.Header onClose={onClose}>
                    <div className="flex items-center gap-2 flex-wrap">
                        <span>Reporte #{diagnosticoId}</span>
                        {d?.hay_falla && <BadgeEstado estado={d.estado} label={d.estado_label} />}
                        {d?.critico && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-medium bg-red-50 text-red-600">
                                <ShieldAlert size={12} /> Crítico
                            </span>
                        )}
                    </div>
                </Modal.Header>
                <Modal.Body>
                    {cargando || !d ? (
                        <div className="flex justify-center py-16"><Loader2 size={24} className="animate-spin text-sura-blue" /></div>
                    ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                            {/* Datos del reporte */}
                            <div className="lg:col-span-2 space-y-4">
                                <div>
                                    <p className="font-semibold text-gray-900">{etiqueta(persona) || 'Sin nombre'}</p>
                                    <p className="text-xs text-gray-500">
                                        {[persona.departamento_real, persona.ficha && `Ficha ${persona.ficha}`].filter(Boolean).join(' · ')}
                                    </p>
                                    <p className="text-xs text-gray-400 mt-1 flex items-center gap-1"><Calendar size={11} /> {fechaHora(d.created_at)}</p>
                                </div>

                                <div className="space-y-2.5 p-4 bg-gray-50 rounded-xl border border-gray-100">
                                    {d.hay_falla && (
                                        <div className="flex items-start gap-2">
                                            <Package size={15} className="text-gray-400 mt-0.5 shrink-0" />
                                            <QueSeReporto linea={d.linea_label} desviaciones={d.desviaciones_detalle} className="text-sm" />
                                        </div>
                                    )}
                                    {(d.lote || d.fecha_fabricacion) && (
                                        <Dato icon={Tag}>
                                            {d.lote
                                                ? `Lote ${d.lote}`
                                                : `Sin lote · fabricado el ${new Date(d.fecha_fabricacion + 'T00:00:00').toLocaleDateString('es-VE')}`}
                                        </Dato>
                                    )}
                                    {(d.region || d.establecimiento) && (
                                        <Dato icon={MapPin}>{[d.region, d.establecimiento].filter(Boolean).join(' · ')}</Dato>
                                    )}
                                </div>

                                {fotos.length > 0 && (
                                    <div className="flex gap-2 flex-wrap">
                                        {fotos.map((src, i) => (
                                            <img
                                                key={src}
                                                src={src}
                                                alt={`Foto ${i + 1} del hallazgo`}
                                                className="w-20 h-20 rounded-xl object-cover border border-gray-200 cursor-zoom-in"
                                                onClick={() => setVisor(src)}
                                            />
                                        ))}
                                    </div>
                                )}

                                <div className="space-y-2">
                                    {d.capsulas_label && <Dato icon={GraduationCap}>{d.capsulas_label}</Dato>}
                                    <Dato icon={MessageSquare}>
                                        <span className="text-gray-500">Sugerencia:</span>{' '}
                                        {d.sugerencia || <span className="text-gray-400">Sin sugerencia</span>}
                                    </Dato>
                                </div>

                                {d.tomado_por && (
                                    <p className="flex items-center gap-1.5 text-xs text-gray-500">
                                        <UserCheck size={13} className="text-gray-400" />
                                        Atendido por {etiqueta(d.tomado_por)}{d.tomado_at ? ` el ${fechaHora(d.tomado_at)}` : ''}
                                    </p>
                                )}

                                {cerrado && (
                                    <div className="p-4 rounded-xl border border-emerald-100 bg-emerald-50/60">
                                        <p className="flex items-center gap-1.5 text-sm font-semibold text-emerald-800">
                                            <CheckCircle size={15} /> Cerrado por {etiqueta(d.cerrado_por) || 'Calidad'}
                                        </p>
                                        {d.cerrado_at && <p className="text-xs text-emerald-700 mt-0.5">{fechaHora(d.cerrado_at)}</p>}
                                        {d.nota_cierre && <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">{d.nota_cierre}</p>}
                                    </div>
                                )}
                            </div>

                            {/* Conversacion (solo reportes con falla) */}
                            {!conSeguimiento ? (
                                <div className="lg:col-span-3 flex items-center justify-center text-center p-6 rounded-2xl bg-gray-50 border border-gray-100">
                                    <p className="text-sm text-gray-500">Diagnóstico sin falla: no lleva seguimiento ni conversación.</p>
                                </div>
                            ) : (
                            <div className="lg:col-span-3 flex flex-col">
                                <h3 className="flex items-center gap-2 font-semibold text-gray-900 mb-3">
                                    <MessageSquare size={16} className="text-gray-400" />
                                    Conversación ({comentarios.length})
                                </h3>
                                <div className="flex-1 space-y-3 max-h-[420px] overflow-y-auto pr-1">
                                    {comentarios.length === 0 && (
                                        <p className="text-sm text-gray-400 py-6 text-center">
                                            {d.puede_gestionar
                                                ? 'Aún no hay mensajes. Si tienes una duda sobre el reporte, escríbele a quien lo hizo.'
                                                : 'Aún no hay mensajes. Si Calidad tiene una duda, te escribirá aquí.'}
                                        </p>
                                    )}
                                    {comentarios.map((c, i) => (
                                        <motion.div
                                            key={c.id}
                                            initial={{ opacity: 0, y: 8 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: Math.min(i, 8) * 0.03 }}
                                            className={`p-4 rounded-2xl border ${c.es_calidad ? 'bg-blue-50/60 border-blue-100 border-l-[3px] border-l-sura-blue' : 'bg-gray-50 border-gray-100'}`}
                                        >
                                            <div className="flex items-center gap-2 mb-1.5 text-sm">
                                                <strong className="text-gray-900">{etiqueta(c.autor) || 'Sin nombre'}</strong>
                                                {c.es_calidad && <span className="px-2 py-0.5 rounded-md text-[11px] font-bold bg-blue-100 text-sura-blue">Calidad</span>}
                                                <span className="ml-auto text-xs text-gray-400">{fechaHora(c.created_at)}</span>
                                            </div>
                                            <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{c.mensaje}</p>
                                        </motion.div>
                                    ))}
                                    <div ref={finRef} />
                                </div>

                                {cerrado ? (
                                    <p className="mt-4 flex items-center justify-center gap-1.5 text-sm text-gray-400 py-3 border-t border-gray-100">
                                        <Lock size={14} /> El reporte está cerrado
                                    </p>
                                ) : (
                                    <div className="mt-4 pt-4 border-t border-gray-100">
                                        <div className="flex items-end gap-2 bg-gray-50 border-[1.5px] border-gray-200 rounded-2xl p-2 focus-within:border-sura-blue focus-within:bg-white transition-colors">
                                            <textarea
                                                value={mensaje}
                                                onChange={e => setMensaje(e.target.value)}
                                                onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); enviar(); } }}
                                                rows={2}
                                                maxLength={2000}
                                                placeholder={d.puede_gestionar ? 'Escribe una pregunta para quien reportó…' : 'Escribe tu respuesta para Calidad…'}
                                                className="flex-1 resize-none bg-transparent outline-none text-sm text-gray-800 placeholder-gray-400 px-1.5 py-1.5 max-h-40"
                                            />
                                            <button
                                                type="button"
                                                onClick={enviar}
                                                disabled={!mensaje.trim() || enviando}
                                                title="Enviar (Ctrl+Enter)"
                                                className="w-10 h-10 shrink-0 flex items-center justify-center rounded-xl bg-sura-blue text-white hover:bg-blue-700 transition-colors disabled:opacity-25 disabled:cursor-not-allowed"
                                            >
                                                {enviando ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                                            </button>
                                        </div>
                                        <p className="text-[11px] text-gray-400 mt-1.5 text-right">Ctrl+Enter para enviar</p>
                                    </div>
                                )}
                            </div>
                            )}
                        </div>
                    )}
                </Modal.Body>
                {d && conSeguimiento && d.puede_gestionar && !cerrado && (
                    <Modal.Footer>
                        {cerrando ? (
                            <div className="w-full space-y-3">
                                <label className="block text-sm font-medium text-gray-700">Nota de cierre</label>
                                <textarea
                                    value={nota}
                                    onChange={e => setNota(e.target.value)}
                                    rows={3}
                                    maxLength={2000}
                                    autoFocus
                                    placeholder="Qué se hizo o se concluyó con este reporte"
                                    className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                                />
                                <div className="flex items-center gap-3">
                                    <button
                                        type="button"
                                        onClick={() => { setCerrando(false); setNota(''); }}
                                        className="flex-1 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="button"
                                        onClick={cerrar}
                                        disabled={!nota.trim() || accion === 'cerrar'}
                                        className="flex-1 py-2.5 bg-sura-blue text-white rounded-xl font-medium hover:bg-blue-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                    >
                                        {accion === 'cerrar' ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                                        Confirmar cierre
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-center gap-3 w-full">
                                <button
                                    type="button"
                                    onClick={() => setCerrando(true)}
                                    className="flex-1 py-2.5 bg-sura-blue text-white rounded-xl font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                                >
                                    <CheckCircle size={16} />
                                    Cerrar reporte
                                </button>
                            </div>
                        )}
                    </Modal.Footer>
                )}
            </Modal>

            {visor && createPortal((
                <div
                    className="fixed inset-0 bg-black/80 flex items-center justify-center z-[1100] p-4 cursor-zoom-out"
                    onClick={() => setVisor(null)}
                >
                    <img src={visor} alt="Foto del hallazgo" className="max-w-[90vw] max-h-[85vh] object-contain rounded-xl" />
                </div>
            ), document.body)}
        </>
    );
};

export default DetalleReporte;
